import { useMemo, type CSSProperties } from "react";
import type { LpcLayers } from "../../infra/api.js";

// === Color palettes with shading variants ===

interface Shade {
  base: string;
  hi: string;
  shade: string;
  line: string;
}

export const SKIN_SHADES = {
  light: { base: "#ffd6b3", hi: "#ffe8d0", shade: "#d9a880", line: "#8a5a3a" },
  tan: { base: "#e0b896", hi: "#f0d4ba", shade: "#b88c6a", line: "#70472e" },
  brown: { base: "#a07050", hi: "#b8886a", shade: "#7a4f30", line: "#4a2810" },
  dark: { base: "#664030", hi: "#7a5240", shade: "#48281a", line: "#2a1408" },
} satisfies Record<string, Shade>;

export const HAIR_SHADES = {
  black: { base: "#2a2422", hi: "#403838", shade: "#100a08", line: "#100a08" },
  brown: { base: "#5a3820", hi: "#704830", shade: "#3a2010", line: "#2a1408" },
  blonde: { base: "#e0c060", hi: "#f4d878", shade: "#a88840", line: "#604820" },
  red: { base: "#c04820", hi: "#e05830", shade: "#80300c", line: "#481008" },
  white: { base: "#f0e4d0", hi: "#fff8e8", shade: "#a89880", line: "#604820" },
  pink: { base: "#f070a0", hi: "#ff90b8", shade: "#c04878", line: "#702848" },
  blue: { base: "#5080d0", hi: "#70a0f0", shade: "#305898", line: "#1c305c" },
  purple: { base: "#9050c8", hi: "#a868e0", shade: "#603088", line: "#301848" },
  green: { base: "#509050", hi: "#70b070", shade: "#306838", line: "#183818" },
  silver: { base: "#b8b8c0", hi: "#d8d8e0", shade: "#888890", line: "#484850" },
} satisfies Record<string, Shade>;

export const FABRIC_SHADES = {
  white: { base: "#ede5d2", hi: "#fff8e8", shade: "#c0b8a0", line: "#807860" },
  black: { base: "#252025", hi: "#3a3438", shade: "#0e0c0e", line: "#0a0a0a" },
  red: { base: "#c02030", hi: "#dc3040", shade: "#80101c", line: "#480810" },
  blue: { base: "#3050b0", hi: "#5070d0", shade: "#1c3878", line: "#0c1c40" },
  green: { base: "#308040", hi: "#48a058", shade: "#1c5028", line: "#0a2810" },
  grey: { base: "#606060", hi: "#787878", shade: "#404040", line: "#202020" },
  yellow: { base: "#e8b830", hi: "#f8d040", shade: "#a08018", line: "#604808" },
  purple: { base: "#7038a0", hi: "#9050c0", shade: "#481c70", line: "#240c3c" },
  pink: { base: "#e060a0", hi: "#f078b8", shade: "#a83878", line: "#581838" },
  orange: { base: "#e87028", hi: "#f88838", shade: "#b04810", line: "#682808" },
  teal: { base: "#308088", hi: "#4ca0a8", shade: "#185860", line: "#0c2c30" },
  brown: { base: "#7a4828", hi: "#965a36", shade: "#502810", line: "#28100a" },
} satisfies Record<string, Shade>;

export const ACCESSORY_SHADES = {
  none: { base: "transparent", hi: "transparent", shade: "transparent", line: "transparent" },
  glasses: { base: "#1a1410", hi: "#3a3030", shade: "#0a0808", line: "#0a0808" },
  shades: { base: "#1a1410", hi: "#3a3030", shade: "#0a0808", line: "#0a0808" },
  hat: { base: "#1a1410", hi: "#3a3030", shade: "#0a0808", line: "#0a0808" },
  cap: { base: "#3050b0", hi: "#5070d0", shade: "#1c3878", line: "#0c1c40" },
  headphones: { base: "#202020", hi: "#404040", shade: "#0a0a0a", line: "#0a0a0a" },
  bandana: { base: "#c02030", hi: "#dc3040", shade: "#80101c", line: "#480810" },
  beanie: { base: "#308040", hi: "#48a058", shade: "#1c5028", line: "#0a2810" },
} satisfies Record<string, Shade>;

// Flat exports for AvatarBuilder swatches
export const BODY_COLORS = {
  light: SKIN_SHADES.light.base,
  tan: SKIN_SHADES.tan.base,
  brown: SKIN_SHADES.brown.base,
  dark: SKIN_SHADES.dark.base,
};
export const HAIR_COLORS = {
  black: HAIR_SHADES.black.base,
  brown: HAIR_SHADES.brown.base,
  blonde: HAIR_SHADES.blonde.base,
  red: HAIR_SHADES.red.base,
  white: HAIR_SHADES.white.base,
  pink: HAIR_SHADES.pink.base,
  blue: HAIR_SHADES.blue.base,
  purple: HAIR_SHADES.purple.base,
  green: HAIR_SHADES.green.base,
  silver: HAIR_SHADES.silver.base,
};
export const FABRIC_COLORS = {
  white: FABRIC_SHADES.white.base,
  black: FABRIC_SHADES.black.base,
  red: FABRIC_SHADES.red.base,
  blue: FABRIC_SHADES.blue.base,
  green: FABRIC_SHADES.green.base,
  grey: FABRIC_SHADES.grey.base,
  yellow: FABRIC_SHADES.yellow.base,
  purple: FABRIC_SHADES.purple.base,
  pink: FABRIC_SHADES.pink.base,
  orange: FABRIC_SHADES.orange.base,
  teal: FABRIC_SHADES.teal.base,
  brown: FABRIC_SHADES.brown.base,
};

export const BODY_STYLES = ["light", "tan", "brown", "dark"] as const;
export const HAIR_STYLES = [
  "short",
  "long",
  "ponytail",
  "bun",
  "mohawk",
  "afro",
  "sidecut",
  "bald",
] as const;
export const SHIRT_STYLES = ["tshirt", "longsleeve", "tank", "hoodie", "blazer", "dress"] as const;
export const PANTS_STYLES = ["jeans", "shorts", "skirt", "cargo", "bellbottom"] as const;
export const ACCESSORY_STYLES = [
  "none",
  "glasses",
  "shades",
  "hat",
  "cap",
  "headphones",
  "bandana",
  "beanie",
] as const;

export type AvatarAction =
  | "idle"
  | "play-vocal"
  | "play-guitar"
  | "play-bass"
  | "play-drums"
  | "play-keys";

interface Props {
  layers: LpcLayers;
  size?: number;
  label?: string | undefined;
  action?: AvatarAction;
  instrument?: "vocal" | "guitar" | "bass" | "drums" | "keys";
}

function pickShade(map: Record<string, Shade>, key: string, fallback: Shade): Shade {
  const v = map[key];
  return v ?? fallback;
}

export function LpcAvatar({
  layers,
  size = 64,
  label,
  action = "idle",
  instrument,
}: Props): JSX.Element {
  const skin = pickShade(SKIN_SHADES, layers.body.id, SKIN_SHADES.light);
  const hair = pickShade(HAIR_SHADES, layers.hair.color, HAIR_SHADES.black);
  const shirt = pickShade(FABRIC_SHADES, layers.shirt.color, FABRIC_SHADES.white);
  const pants = pickShade(FABRIC_SHADES, layers.pants.color, FABRIC_SHADES.blue);
  const accessoryId = layers.accessory?.id ?? "none";
  const accessoryColor = layers.accessory?.color;
  const accessoryShade = accessoryColor
    ? pickShade(FABRIC_SHADES, accessoryColor, ACCESSORY_SHADES.hat)
    : pickShade(ACCESSORY_SHADES, accessoryId, ACCESSORY_SHADES.none);

  const animClass =
    action === "idle" ? "lpc-idle" : `lpc-play lpc-play-${action.replace("play-", "")}`;

  // Random per-instance offsets so a roster of avatars doesn't breathe/blink in lockstep.
  const offsets = useMemo(
    () => ({
      breathe: (Math.random() * 1.8).toFixed(2),
      blink: (Math.random() * 4.5).toFixed(2),
      sway: (Math.random() * 4).toFixed(2),
      arm: (Math.random() * 3).toFixed(2),
    }),
    [],
  );

  const style: CSSProperties = {
    width: size,
    height: size,
    "--idle-breathe": `${offsets.breathe}s`,
    "--idle-blink": `${offsets.blink}s`,
    "--idle-sway": `${offsets.sway}s`,
    "--idle-arm": `${offsets.arm}s`,
  } as CSSProperties;

  return (
    <div
      className={`lpc-avatar ${animClass}`}
      style={style}
      title={label}
      role="img"
      aria-label={label}
    >
      <svg
        viewBox="0 0 40 40"
        width={size}
        height={size}
        shapeRendering="crispEdges"
        style={{ display: "block", imageRendering: "pixelated" as const, overflow: "visible" }}
      >
        <ellipse cx="20" cy="38.5" rx="9" ry="1" fill="rgba(0,0,0,0.4)" />
        <Pants style={layers.pants.id} pants={pants} skin={skin} />
        <Shoes pantsId={layers.pants.id} />
        <Body skin={skin} />
        <Shirt style={layers.shirt.id} shirt={shirt} skin={skin} />
        <g className="lpc-head-stack">
          <Head skin={skin} />
          <Hair style={layers.hair.id} hair={hair} />
          <Accessory id={accessoryId} shade={accessoryShade} hair={hair} />
        </g>
        {instrument && <Instrument kind={instrument} />}
      </svg>
    </div>
  );
}

// ============= HEAD =============

function Head({ skin }: { skin: Shade }): JSX.Element {
  return (
    <g>
      <rect x="18" y="17" width="4" height="3" fill={skin.shade} />
      <rect x="18" y="17" width="4" height="1" fill={skin.line} />
      <rect x="14" y="6" width="12" height="12" fill={skin.base} />
      <rect x="15" y="6" width="10" height="1" fill={skin.line} />
      <rect x="14" y="7" width="1" height="10" fill={skin.line} />
      <rect x="25" y="7" width="1" height="10" fill={skin.line} />
      <rect x="15" y="17" width="10" height="1" fill={skin.line} />
      {/* Ears */}
      <rect x="13" y="11" width="1" height="3" fill={skin.shade} />
      <rect x="13" y="11" width="1" height="1" fill={skin.line} />
      <rect x="13" y="13" width="1" height="1" fill={skin.line} />
      <rect x="26" y="11" width="1" height="3" fill={skin.shade} />
      <rect x="26" y="11" width="1" height="1" fill={skin.line} />
      <rect x="26" y="13" width="1" height="1" fill={skin.line} />
      {/* Shading */}
      <rect x="23" y="8" width="2" height="9" fill={skin.shade} opacity="0.45" />
      <rect x="15" y="13" width="2" height="2" fill={skin.hi} opacity="0.6" />
      {/* Eyebrows */}
      <rect x="16" y="9" width="2" height="1" fill={skin.line} opacity="0.7" />
      <rect x="22" y="9" width="2" height="1" fill={skin.line} opacity="0.7" />
      {/* Eyes (each in its own group so they blink from their own center via scaleY) */}
      <g className="lpc-eye">
        <rect x="16" y="11" width="2" height="2" fill="#1a1410" />
        <rect x="17" y="11" width="1" height="1" fill="#ffffff" />
      </g>
      <g className="lpc-eye">
        <rect x="22" y="11" width="2" height="2" fill="#1a1410" />
        <rect x="23" y="11" width="1" height="1" fill="#ffffff" />
      </g>
      {/* Nose */}
      <rect x="19" y="13" width="2" height="2" fill={skin.shade} opacity="0.7" />
      <rect x="19" y="14" width="1" height="1" fill={skin.line} opacity="0.5" />
      {/* Mouth */}
      <rect x="18" y="16" width="4" height="1" fill={skin.line} />
      <rect x="19" y="16" width="2" height="1" fill="#7a2820" opacity="0.6" />
    </g>
  );
}

// ============= HAIR =============

function Hair({ style, hair }: { style: string; hair: Shade }): JSX.Element {
  if (style === "bald") return <></>;

  if (style === "long") {
    return (
      <g>
        <rect x="13" y="4" width="14" height="6" fill={hair.base} />
        <rect x="14" y="3" width="12" height="1" fill={hair.base} />
        <rect x="13" y="4" width="14" height="1" fill={hair.line} />
        <rect x="12" y="6" width="2" height="11" fill={hair.base} />
        <rect x="26" y="6" width="2" height="11" fill={hair.base} />
        <rect x="12" y="6" width="1" height="11" fill={hair.line} />
        <rect x="27" y="6" width="1" height="11" fill={hair.line} />
        <rect x="12" y="15" width="16" height="3" fill={hair.base} />
        <rect x="11" y="16" width="18" height="2" fill={hair.base} />
        <rect x="11" y="17" width="18" height="1" fill={hair.line} />
        <rect x="15" y="8" width="10" height="1" fill={hair.shade} opacity="0.8" />
        <rect x="16" y="4" width="2" height="2" fill={hair.hi} />
        <rect x="22" y="5" width="1" height="2" fill={hair.hi} />
      </g>
    );
  }

  if (style === "ponytail") {
    return (
      <g>
        <rect x="14" y="4" width="12" height="5" fill={hair.base} />
        <rect x="15" y="3" width="10" height="1" fill={hair.base} />
        <rect x="14" y="4" width="12" height="1" fill={hair.line} />
        <rect x="14" y="9" width="2" height="1" fill={hair.base} />
        <rect x="24" y="9" width="2" height="1" fill={hair.base} />
        <rect x="17" y="4" width="2" height="1" fill={hair.hi} />
        <rect x="26" y="9" width="3" height="2" fill={hair.base} />
        <rect x="27" y="11" width="2" height="6" fill={hair.base} />
        <rect x="28" y="13" width="1" height="4" fill={hair.shade} />
        <rect x="26" y="17" width="3" height="1" fill={hair.shade} />
        <rect x="26" y="9" width="3" height="1" fill={hair.line} />
      </g>
    );
  }

  if (style === "bun") {
    return (
      <g>
        {/* Top hair */}
        <rect x="14" y="4" width="12" height="5" fill={hair.base} />
        <rect x="15" y="3" width="10" height="1" fill={hair.base} />
        <rect x="14" y="4" width="12" height="1" fill={hair.line} />
        <rect x="14" y="9" width="2" height="1" fill={hair.base} />
        <rect x="24" y="9" width="2" height="1" fill={hair.base} />
        <rect x="17" y="4" width="2" height="1" fill={hair.hi} />
        {/* Bun on top */}
        <ellipse cx="20" cy="3" rx="4" ry="2.5" fill={hair.base} />
        <ellipse cx="20" cy="3" rx="4" ry="2.5" fill="none" stroke={hair.line} strokeWidth="0.4" />
        <ellipse cx="19" cy="2.3" rx="1.5" ry="0.8" fill={hair.hi} opacity="0.6" />
        {/* Hair wrap line */}
        <rect x="16" y="5" width="8" height="0.4" fill={hair.line} />
      </g>
    );
  }

  if (style === "mohawk") {
    return (
      <g>
        {/* Shaved sides — very thin layer */}
        <rect x="14" y="6" width="12" height="1" fill={hair.shade} opacity="0.4" />
        <rect x="14" y="4" width="2" height="3" fill={hair.shade} />
        <rect x="24" y="4" width="2" height="3" fill={hair.shade} />
        {/* Spike center */}
        <rect x="18" y="0" width="4" height="9" fill={hair.base} />
        <rect x="18" y="0" width="4" height="1" fill={hair.line} />
        <rect x="17" y="2" width="1" height="7" fill={hair.base} />
        <rect x="22" y="2" width="1" height="7" fill={hair.base} />
        <rect x="19" y="0" width="1" height="9" fill={hair.hi} />
        {/* Spikes */}
        <polygon points="18,0 19,-2 20,0" fill={hair.base} />
        <polygon points="20,0 21,-1 22,0" fill={hair.base} />
      </g>
    );
  }

  if (style === "afro") {
    return (
      <g>
        {/* Large volume cloud */}
        <ellipse cx="20" cy="6" rx="10" ry="6" fill={hair.base} />
        <ellipse cx="20" cy="6" rx="10" ry="6" fill="none" stroke={hair.line} strokeWidth="0.5" />
        {/* Bumps for texture */}
        <circle cx="13" cy="5" r="2" fill={hair.base} stroke={hair.line} strokeWidth="0.3" />
        <circle cx="27" cy="5" r="2" fill={hair.base} stroke={hair.line} strokeWidth="0.3" />
        <circle cx="20" cy="2" r="2.5" fill={hair.base} stroke={hair.line} strokeWidth="0.3" />
        <circle cx="15" cy="3" r="1.8" fill={hair.base} stroke={hair.line} strokeWidth="0.3" />
        <circle cx="25" cy="3" r="1.8" fill={hair.base} stroke={hair.line} strokeWidth="0.3" />
        {/* Highlights */}
        <circle cx="17" cy="4" r="1" fill={hair.hi} opacity="0.6" />
        <circle cx="23" cy="5" r="0.8" fill={hair.hi} opacity="0.4" />
        {/* Forehead shadow */}
        <rect x="15" y="8" width="10" height="1" fill={hair.shade} opacity="0.7" />
      </g>
    );
  }

  if (style === "sidecut") {
    return (
      <g>
        {/* Right side: full hair */}
        <rect x="20" y="4" width="6" height="5" fill={hair.base} />
        <rect x="22" y="3" width="3" height="1" fill={hair.base} />
        <rect x="20" y="4" width="6" height="1" fill={hair.line} />
        <rect x="24" y="9" width="2" height="2" fill={hair.base} />
        <rect x="22" y="4" width="2" height="1" fill={hair.hi} />
        {/* Left side: shaved/dark stubble */}
        <rect x="14" y="6" width="6" height="3" fill={hair.shade} opacity="0.6" />
        <rect x="14" y="5" width="6" height="1" fill={hair.shade} opacity="0.4" />
        {/* Parted line */}
        <rect x="19" y="4" width="1" height="5" fill={hair.line} />
        {/* Forehead drop on right */}
        <rect x="22" y="8" width="3" height="2" fill={hair.base} />
      </g>
    );
  }

  // short (default)
  return (
    <g>
      <rect x="14" y="4" width="12" height="5" fill={hair.base} />
      <rect x="15" y="3" width="10" height="1" fill={hair.base} />
      <rect x="14" y="4" width="12" height="1" fill={hair.line} />
      <rect x="14" y="9" width="2" height="2" fill={hair.base} />
      <rect x="24" y="9" width="2" height="2" fill={hair.base} />
      <rect x="16" y="8" width="2" height="1" fill={hair.base} />
      <rect x="22" y="8" width="2" height="1" fill={hair.base} />
      <rect x="17" y="4" width="3" height="1" fill={hair.hi} />
      <rect x="15" y="9" width="10" height="1" fill={hair.shade} opacity="0.5" />
    </g>
  );
}

// ============= ACCESSORY (face/head extra) =============

function Accessory({ id, shade, hair }: { id: string; shade: Shade; hair: Shade }): JSX.Element {
  if (id === "none") return <></>;

  if (id === "glasses") {
    return (
      <g className="lpc-acc lpc-glasses">
        <rect
          x="15"
          y="11"
          width="4"
          height="3"
          fill="none"
          stroke={shade.line}
          strokeWidth="0.6"
        />
        <rect
          x="21"
          y="11"
          width="4"
          height="3"
          fill="none"
          stroke={shade.line}
          strokeWidth="0.6"
        />
        <rect x="19" y="12" width="2" height="0.6" fill={shade.line} />
        {/* Glass highlight */}
        <rect x="16" y="11.5" width="1" height="1" fill="#ffffff" opacity="0.4" />
        <rect x="22" y="11.5" width="1" height="1" fill="#ffffff" opacity="0.4" />
      </g>
    );
  }

  if (id === "shades") {
    return (
      <g className="lpc-acc lpc-shades">
        <rect x="15" y="11" width="4" height="2.5" fill={shade.base} />
        <rect x="21" y="11" width="4" height="2.5" fill={shade.base} />
        <rect x="19" y="12" width="2" height="0.6" fill={shade.line} />
        {/* Reflection */}
        <rect x="16" y="11" width="1" height="1" fill="#ffffff" opacity="0.5" />
        <rect x="22" y="11" width="1" height="1" fill="#ffffff" opacity="0.5" />
        {/* Top frame */}
        <rect x="14" y="10.5" width="12" height="0.6" fill={shade.line} />
      </g>
    );
  }

  if (id === "hat") {
    // Top hat / fedora
    return (
      <g className="lpc-acc lpc-hat">
        {/* Brim */}
        <rect x="11" y="6" width="18" height="1.5" fill={shade.base} />
        <rect x="11" y="7" width="18" height="0.4" fill={shade.line} />
        {/* Crown */}
        <rect x="14" y="1" width="12" height="6" fill={shade.base} />
        <rect x="14" y="1" width="12" height="0.5" fill={shade.hi} />
        <rect x="14" y="6" width="12" height="0.5" fill={shade.line} />
        {/* Band */}
        <rect x="14" y="4.5" width="12" height="1" fill="#e8443a" />
        <rect x="14" y="4.5" width="12" height="0.3" fill="#a82820" />
      </g>
    );
  }

  if (id === "cap") {
    // Baseball cap
    return (
      <g className="lpc-acc lpc-cap">
        <rect x="13" y="4" width="14" height="3.5" fill={shade.base} />
        <rect x="13" y="4" width="14" height="0.5" fill={shade.hi} />
        <rect x="13" y="7" width="14" height="0.4" fill={shade.line} />
        {/* Visor */}
        <rect x="11" y="7" width="14" height="1.2" fill={shade.shade} />
        <rect x="11" y="8" width="14" height="0.4" fill={shade.line} />
        {/* Logo dot */}
        <circle cx="20" cy="5.7" r="0.7" fill="#f4ebd9" />
      </g>
    );
  }

  if (id === "headphones") {
    return (
      <g className="lpc-acc lpc-headphones">
        {/* Band over head */}
        <path
          d="M 13,4 Q 20,1.5 27,4"
          fill="none"
          stroke={shade.base}
          strokeWidth="1.2"
          strokeLinecap="round"
        />
        <path
          d="M 13,4 Q 20,1.5 27,4"
          fill="none"
          stroke={shade.line}
          strokeWidth="0.4"
          strokeLinecap="round"
        />
        {/* Ear cups */}
        <rect x="11" y="9" width="3" height="4" fill={shade.base} rx="0.5" />
        <rect x="26" y="9" width="3" height="4" fill={shade.base} rx="0.5" />
        <rect x="11" y="9" width="3" height="0.5" fill={shade.hi} />
        <rect x="26" y="9" width="3" height="0.5" fill={shade.hi} />
        {/* Accent dots */}
        <circle cx="12.5" cy="11" r="0.5" fill="#f0c84a" />
        <circle cx="27.5" cy="11" r="0.5" fill="#f0c84a" />
      </g>
    );
  }

  if (id === "bandana") {
    return (
      <g className="lpc-acc lpc-bandana">
        <rect x="13" y="7" width="14" height="3" fill={shade.base} />
        <rect x="13" y="7" width="14" height="0.5" fill={shade.hi} />
        <rect x="13" y="9.5" width="14" height="0.5" fill={shade.line} />
        {/* Knot on the side */}
        <polygon points="26,7 30,5 30,11 26,9" fill={shade.base} />
        <polygon points="26,7 30,5 30,11 26,9" fill="none" stroke={shade.line} strokeWidth="0.3" />
        {/* Pattern dots */}
        <circle cx="16" cy="8.5" r="0.4" fill="#f4ebd9" opacity="0.7" />
        <circle cx="20" cy="8.5" r="0.4" fill="#f4ebd9" opacity="0.7" />
        <circle cx="24" cy="8.5" r="0.4" fill="#f4ebd9" opacity="0.7" />
        {/* Hair peek on top */}
        <rect x="14" y="4" width="12" height="3" fill={hair.base} opacity="0.9" />
      </g>
    );
  }

  if (id === "beanie") {
    return (
      <g className="lpc-acc lpc-beanie">
        <rect x="13" y="3" width="14" height="6" fill={shade.base} />
        <rect x="13" y="3" width="14" height="0.5" fill={shade.hi} />
        {/* Brim (folded) */}
        <rect x="13" y="7.5" width="14" height="1.5" fill={shade.shade} />
        <rect x="13" y="7.5" width="14" height="0.4" fill={shade.line} />
        {/* Pom-pom */}
        <circle cx="20" cy="2" r="1.5" fill={shade.base} />
        <circle cx="20" cy="2" r="1.5" fill="none" stroke={shade.line} strokeWidth="0.3" />
        {/* Knit stripes */}
        <rect x="13" y="4" width="14" height="0.3" fill={shade.shade} opacity="0.7" />
        <rect x="13" y="6" width="14" height="0.3" fill={shade.shade} opacity="0.7" />
      </g>
    );
  }

  return <></>;
}

// ============= BODY =============

function Body({ skin }: { skin: Shade }): JSX.Element {
  return (
    <g>
      <rect x="13" y="20" width="14" height="9" fill={skin.base} />
      <g className="lpc-arm-left">
        <rect x="10" y="20" width="3" height="8" fill={skin.base} />
        <rect x="10" y="20" width="1" height="8" fill={skin.line} />
        <rect x="12" y="20" width="1" height="8" fill={skin.shade} opacity="0.4" />
        <rect x="10" y="28" width="3" height="2" fill={skin.base} />
        <rect x="10" y="29" width="3" height="1" fill={skin.shade} />
        <rect x="10" y="30" width="1" height="1" fill={skin.line} />
        <rect x="12" y="30" width="1" height="1" fill={skin.line} />
      </g>
      <g className="lpc-arm-right">
        <rect x="27" y="20" width="3" height="8" fill={skin.base} />
        <rect x="29" y="20" width="1" height="8" fill={skin.line} />
        <rect x="27" y="20" width="1" height="8" fill={skin.hi} opacity="0.4" />
        <rect x="27" y="28" width="3" height="2" fill={skin.base} />
        <rect x="27" y="29" width="3" height="1" fill={skin.shade} />
        <rect x="27" y="30" width="1" height="1" fill={skin.line} />
        <rect x="29" y="30" width="1" height="1" fill={skin.line} />
      </g>
    </g>
  );
}

// ============= SHIRT =============

function Shirt({ style, shirt, skin }: { style: string; shirt: Shade; skin: Shade }): JSX.Element {
  const torso = (
    <>
      <rect x="13" y="20" width="14" height="9" fill={shirt.base} />
      <rect x="13" y="20" width="14" height="1" fill={shirt.line} />
      <rect x="13" y="28" width="14" height="1" fill={shirt.shade} />
      <rect x="14" y="21" width="4" height="1" fill={shirt.hi} opacity="0.7" />
      <rect x="24" y="22" width="3" height="6" fill={shirt.shade} opacity="0.4" />
      <polygon points="18,20 22,20 21,22 19,22" fill={skin.base} />
      <polygon points="18,20 22,20 21,21 19,21" fill={skin.shade} opacity="0.4" />
    </>
  );

  if (style === "longsleeve") {
    return (
      <g>
        {torso}
        <rect x="10" y="20" width="3" height="9" fill={shirt.base} />
        <rect x="27" y="20" width="3" height="9" fill={shirt.base} />
        <rect x="10" y="20" width="1" height="9" fill={shirt.line} />
        <rect x="29" y="20" width="1" height="9" fill={shirt.line} />
        <rect x="10" y="28" width="3" height="1" fill={shirt.shade} />
        <rect x="27" y="28" width="3" height="1" fill={shirt.shade} />
      </g>
    );
  }

  if (style === "tank") {
    // No sleeves; show full upper arm/shoulder skin
    return (
      <g>
        <rect x="14" y="20" width="12" height="9" fill={shirt.base} />
        <rect x="14" y="20" width="12" height="1" fill={shirt.line} />
        <rect x="14" y="28" width="12" height="1" fill={shirt.shade} />
        {/* V-neck wider */}
        <polygon points="17,20 23,20 22,23 18,23" fill={skin.base} />
        <polygon points="17,20 23,20 22,21 18,21" fill={skin.shade} opacity="0.4" />
        {/* Tank straps */}
        <rect x="15" y="19" width="1" height="2" fill={shirt.base} />
        <rect x="24" y="19" width="1" height="2" fill={shirt.base} />
        {/* Body shadow */}
        <rect x="23" y="22" width="3" height="6" fill={shirt.shade} opacity="0.4" />
      </g>
    );
  }

  if (style === "hoodie") {
    return (
      <g>
        {torso}
        {/* Long sleeves */}
        <rect x="10" y="20" width="3" height="9" fill={shirt.base} />
        <rect x="27" y="20" width="3" height="9" fill={shirt.base} />
        <rect x="10" y="20" width="1" height="9" fill={shirt.line} />
        <rect x="29" y="20" width="1" height="9" fill={shirt.line} />
        {/* Hood — drape behind head */}
        <path
          d="M 12,20 Q 12,15 14,14 L 26,14 Q 28,15 28,20 Z"
          fill={shirt.base}
          stroke={shirt.line}
          strokeWidth="0.4"
        />
        <path
          d="M 12,20 Q 12,15 14,14 L 26,14 Q 28,15 28,20"
          fill="none"
          stroke={shirt.hi}
          strokeWidth="0.3"
          opacity="0.5"
        />
        {/* Drawstrings */}
        <rect x="18" y="20" width="0.4" height="3" fill={shirt.line} />
        <rect x="22" y="20" width="0.4" height="3" fill={shirt.line} />
        {/* Pocket */}
        <rect x="16" y="24" width="8" height="3" fill={shirt.shade} opacity="0.6" />
        <rect x="16" y="24" width="8" height="0.4" fill={shirt.line} />
      </g>
    );
  }

  if (style === "blazer") {
    return (
      <g>
        {/* Inner shirt (white) */}
        <rect x="18" y="20" width="4" height="9" fill="#ede5d2" />
        <rect x="19" y="20" width="2" height="1" fill="#807860" />
        {/* Tie */}
        <polygon points="19,20 21,20 21.5,28 18.5,28" fill="#c02030" />
        <polygon points="19,20 21,20 20.5,21 19.5,21" fill="#801020" />
        {/* Blazer body — open at center */}
        <polygon points="13,20 18,20 18,29 13,29" fill={shirt.base} />
        <polygon points="22,20 27,20 27,29 22,29" fill={shirt.base} />
        {/* Lapels */}
        <polygon points="18,20 20,20 18,24" fill={shirt.shade} />
        <polygon points="20,20 22,20 22,24" fill={shirt.shade} />
        {/* Sleeves */}
        <rect x="10" y="20" width="3" height="9" fill={shirt.base} />
        <rect x="27" y="20" width="3" height="9" fill={shirt.base} />
        <rect x="10" y="20" width="1" height="9" fill={shirt.line} />
        <rect x="29" y="20" width="1" height="9" fill={shirt.line} />
        {/* Outline */}
        <rect x="13" y="20" width="5" height="1" fill={shirt.line} />
        <rect x="22" y="20" width="5" height="1" fill={shirt.line} />
        <rect x="13" y="28" width="14" height="1" fill={shirt.line} />
        {/* Pocket */}
        <rect x="14" y="25" width="3" height="0.5" fill={shirt.line} />
        <rect x="23" y="25" width="3" height="0.5" fill={shirt.line} />
        {/* Button */}
        <circle cx="20" cy="24" r="0.4" fill={shirt.line} />
      </g>
    );
  }

  if (style === "dress") {
    return (
      <g>
        {torso}
        <rect x="10" y="20" width="3" height="3" fill={shirt.base} />
        <rect x="27" y="20" width="3" height="3" fill={shirt.base} />
        <rect x="10" y="22" width="3" height="1" fill={shirt.shade} />
        <rect x="27" y="22" width="3" height="1" fill={shirt.shade} />
        <polygon points="12,29 28,29 30,36 10,36" fill={shirt.base} />
        <polygon points="12,29 28,29 28,30 12,30" fill={shirt.line} />
        <rect x="15" y="30" width="1" height="6" fill={shirt.shade} opacity="0.5" />
        <rect x="20" y="30" width="1" height="6" fill={shirt.shade} opacity="0.5" />
        <rect x="24" y="30" width="1" height="6" fill={shirt.shade} opacity="0.5" />
      </g>
    );
  }

  // tshirt (default)
  return (
    <g>
      {torso}
      <rect x="10" y="20" width="3" height="3" fill={shirt.base} />
      <rect x="27" y="20" width="3" height="3" fill={shirt.base} />
      <rect x="10" y="22" width="3" height="1" fill={shirt.shade} />
      <rect x="27" y="22" width="3" height="1" fill={shirt.shade} />
    </g>
  );
}

// ============= PANTS / LEGS =============

function Pants({ style, pants, skin }: { style: string; pants: Shade; skin: Shade }): JSX.Element {
  if (style === "shorts") {
    return (
      <g>
        <rect x="13" y="29" width="6" height="4" fill={pants.base} />
        <rect x="21" y="29" width="6" height="4" fill={pants.base} />
        <rect x="13" y="29" width="14" height="1" fill={pants.line} />
        <rect x="13" y="32" width="14" height="1" fill={pants.shade} />
        <rect x="14" y="33" width="4" height="3" fill={skin.base} />
        <rect x="22" y="33" width="4" height="3" fill={skin.base} />
        <rect x="14" y="33" width="1" height="3" fill={skin.shade} />
        <rect x="22" y="33" width="1" height="3" fill={skin.shade} />
      </g>
    );
  }
  if (style === "skirt") {
    return (
      <g>
        <polygon points="12,29 28,29 30,36 10,36" fill={pants.base} />
        <polygon points="12,29 28,29 28,30 12,30" fill={pants.line} />
        <rect x="15" y="30" width="1" height="6" fill={pants.shade} opacity="0.6" />
        <rect x="20" y="30" width="1" height="6" fill={pants.shade} opacity="0.6" />
        <rect x="24" y="30" width="1" height="6" fill={pants.shade} opacity="0.6" />
        <rect x="15" y="36" width="4" height="1" fill={skin.base} />
        <rect x="21" y="36" width="4" height="1" fill={skin.base} />
      </g>
    );
  }
  if (style === "cargo") {
    return (
      <g>
        <rect x="13" y="29" width="14" height="1" fill={pants.line} />
        <rect x="13" y="30" width="6" height="6" fill={pants.base} />
        <rect x="21" y="30" width="6" height="6" fill={pants.base} />
        <rect x="13" y="30" width="1" height="6" fill={pants.shade} />
        <rect x="26" y="30" width="1" height="6" fill={pants.shade} />
        <rect x="19" y="30" width="2" height="6" fill={pants.shade} />
        {/* Side pockets */}
        <rect x="14" y="32" width="3" height="2" fill={pants.shade} />
        <rect x="14" y="32" width="3" height="0.4" fill={pants.line} />
        <rect x="14" y="33.6" width="3" height="0.4" fill={pants.line} />
        <rect x="23" y="32" width="3" height="2" fill={pants.shade} />
        <rect x="23" y="32" width="3" height="0.4" fill={pants.line} />
        <rect x="23" y="33.6" width="3" height="0.4" fill={pants.line} />
        {/* Buttons on pockets */}
        <circle cx="15.5" cy="33" r="0.3" fill={pants.line} />
        <circle cx="24.5" cy="33" r="0.3" fill={pants.line} />
      </g>
    );
  }
  if (style === "bellbottom") {
    // wider at bottom
    return (
      <g>
        <rect x="13" y="29" width="14" height="1" fill={pants.line} />
        <polygon points="14,30 19,30 20.5,36 12,36" fill={pants.base} />
        <polygon points="21,30 26,30 28,36 19.5,36" fill={pants.base} />
        <rect x="19" y="30" width="2" height="6" fill={pants.shade} />
        {/* Highlight stripe */}
        <rect x="15" y="30" width="1" height="6" fill={pants.hi} opacity="0.4" />
        <rect x="24" y="30" width="1" height="6" fill={pants.hi} opacity="0.4" />
      </g>
    );
  }
  // jeans (default)
  return (
    <g>
      <rect x="13" y="29" width="14" height="1" fill={pants.shade} />
      <rect x="13" y="29" width="14" height="1" fill={pants.line} />
      <rect x="13" y="30" width="6" height="6" fill={pants.base} />
      <rect x="13" y="30" width="1" height="6" fill={pants.shade} />
      <rect x="21" y="30" width="6" height="6" fill={pants.base} />
      <rect x="26" y="30" width="1" height="6" fill={pants.shade} />
      <rect x="19" y="30" width="2" height="6" fill={pants.shade} />
      <rect x="20" y="30" width="1" height="6" fill={pants.line} />
      <rect x="14" y="31" width="2" height="3" fill={pants.hi} opacity="0.5" />
      <rect x="24" y="31" width="2" height="3" fill={pants.hi} opacity="0.5" />
    </g>
  );
}

function Shoes({ pantsId }: { pantsId: string }): JSX.Element {
  if (pantsId === "skirt") {
    return (
      <g>
        <rect x="13" y="37" width="6" height="2" fill="#3a2010" />
        <rect x="21" y="37" width="6" height="2" fill="#3a2010" />
        <rect x="13" y="37" width="6" height="1" fill="#5a3020" />
        <rect x="21" y="37" width="6" height="1" fill="#5a3020" />
      </g>
    );
  }
  if (pantsId === "bellbottom") {
    // shoes hidden under bell, only show heel
    return (
      <g>
        <rect x="12" y="36.5" width="8" height="1.5" fill="#1a1410" />
        <rect x="20" y="36.5" width="8" height="1.5" fill="#1a1410" />
      </g>
    );
  }
  return (
    <g>
      <rect x="12" y="36" width="7" height="3" fill="#1a1410" />
      <rect x="21" y="36" width="7" height="3" fill="#1a1410" />
      <rect x="12" y="38" width="7" height="1" fill="#f0c84a" opacity="0.7" />
      <rect x="21" y="38" width="7" height="1" fill="#f0c84a" opacity="0.7" />
      <rect x="15" y="36" width="2" height="1" fill="#3a2820" />
      <rect x="24" y="36" width="2" height="1" fill="#3a2820" />
    </g>
  );
}

// ============= INSTRUMENTS =============

function Instrument({
  kind,
}: {
  kind: "vocal" | "guitar" | "bass" | "drums" | "keys";
}): JSX.Element {
  switch (kind) {
    case "vocal":
      return (
        <g className="lpc-instrument lpc-mic">
          <rect x="28" y="20" width="1" height="9" fill="#202020" />
          <rect x="28" y="20" width="1" height="9" fill="#404040" opacity="0.5" />
          <rect x="27" y="17" width="3" height="4" fill="#404040" />
          <rect x="27" y="17" width="3" height="1" fill="#606060" />
          <rect x="29" y="17" width="1" height="4" fill="#1a1a1a" />
          <ellipse cx="28.5" cy="15" rx="2.5" ry="2" fill="#a0a0a0" />
          <ellipse
            cx="28.5"
            cy="15"
            rx="2.5"
            ry="2"
            fill="none"
            stroke="#404040"
            strokeWidth="0.3"
          />
          <rect x="27" y="14" width="3" height="0.4" fill="#606060" />
          <rect x="27" y="15" width="3" height="0.4" fill="#606060" />
          <rect x="27" y="16" width="3" height="0.4" fill="#606060" />
          <rect x="27" y="14" width="1" height="1" fill="#e0e0e0" />
        </g>
      );
    case "guitar":
      return (
        <g className="lpc-instrument lpc-guitar">
          <rect x="14" y="20" width="1" height="3" fill="#3a2010" />
          <rect x="25" y="20" width="1" height="3" fill="#3a2010" />
          <ellipse cx="22" cy="25" rx="6" ry="3.5" fill="#000" opacity="0.3" />
          <ellipse cx="22" cy="25" rx="5.5" ry="3" fill="#8a4020" />
          <ellipse cx="22" cy="25" rx="5.5" ry="3" fill="none" stroke="#5a2810" strokeWidth="0.4" />
          <ellipse cx="22" cy="25.5" rx="2.5" ry="1.5" fill="#e0d0a0" />
          <circle cx="22" cy="25" r="0.8" fill="#1a0808" />
          <rect x="24" y="24" width="2" height="0.6" fill="#1a1a1a" />
          <rect x="3" y="23.5" width="14" height="1.3" fill="#5a3010" />
          <rect x="3" y="23.5" width="14" height="0.4" fill="#7a4020" />
          {[4, 7, 10, 13, 16].map((x) => (
            <rect key={x} x={x} y="23.5" width="0.2" height="1.3" fill="#c0c0c0" />
          ))}
          <polygon points="0,22 3,22.5 3,25 0,25.5" fill="#3a2010" />
          <rect x="0.5" y="23" width="1" height="0.4" fill="#e8c547" />
          <rect x="0.5" y="24" width="1" height="0.4" fill="#e8c547" />
          <rect x="3" y="23.8" width="22" height="0.2" fill="#c0c0c0" />
          <rect x="3" y="24.3" width="22" height="0.2" fill="#a0a0a0" />
          <ellipse cx="20" cy="23.5" rx="2" ry="0.6" fill="#d06030" opacity="0.7" />
        </g>
      );
    case "bass":
      return (
        <g className="lpc-instrument lpc-bass">
          <rect x="14" y="20" width="1" height="3" fill="#1a1410" />
          <rect x="25" y="20" width="1" height="3" fill="#1a1410" />
          <ellipse cx="22" cy="25" rx="6.5" ry="3.5" fill="#000" opacity="0.4" />
          <polygon points="17,22.5 27,22.5 28,25 27,27.5 17,27.5 16,25" fill="#101010" />
          <polygon
            points="17,22.5 27,22.5 28,25 27,27.5 17,27.5 16,25"
            fill="none"
            stroke="#3a3038"
            strokeWidth="0.3"
          />
          <rect x="20" y="24.4" width="4" height="1.2" fill="#404040" />
          <rect x="20" y="24.4" width="4" height="0.3" fill="#606060" />
          <rect x="25" y="24.5" width="2" height="1" fill="#a0a0a0" />
          <rect x="3" y="23.2" width="14" height="2" fill="#1a1410" />
          <rect x="3" y="23.2" width="14" height="0.4" fill="#3a2820" />
          <polygon points="0,22 3,22.2 3,25.4 0,25.6" fill="#1a1410" />
          <rect x="3" y="23.7" width="22" height="0.3" fill="#c0c0c0" />
          <rect x="3" y="24.3" width="22" height="0.3" fill="#a0a0a0" />
          <rect x="3" y="24.9" width="22" height="0.3" fill="#909090" />
          <rect x="3" y="25.5" width="14" height="0.3" fill="#808080" />
          <rect x="18" y="22.8" width="4" height="0.4" fill="#3a3038" />
        </g>
      );
    case "drums":
      return (
        <g className="lpc-instrument lpc-drums">
          <rect x="6" y="22" width="0.6" height="8" fill="#404040" />
          <rect x="34" y="22" width="0.6" height="8" fill="#404040" />
          <ellipse cx="6.3" cy="22" rx="3.5" ry="0.7" fill="#c0a020" />
          <ellipse cx="6.3" cy="21.7" rx="3.5" ry="0.7" fill="#e8c547" />
          <circle cx="6.3" cy="22" r="0.4" fill="#604010" />
          <ellipse cx="34.3" cy="22" rx="3.5" ry="0.7" fill="#c0a020" />
          <ellipse cx="34.3" cy="21.7" rx="3.5" ry="0.7" fill="#e8c547" />
          <circle cx="34.3" cy="22" r="0.4" fill="#604010" />
          <rect x="5" y="27" width="6" height="4" fill="#902020" />
          <rect x="5" y="27" width="6" height="0.5" fill="#e0e0e0" />
          <rect x="5" y="27" width="6" height="0.4" fill="#c0c0c0" />
          <rect x="5" y="30.5" width="6" height="0.5" fill="#1a0a0a" />
          <rect x="5" y="27" width="0.6" height="4" fill="#a8a8a8" />
          <rect x="10.4" y="27" width="0.6" height="4" fill="#3a3030" />
          <rect x="17" y="25" width="6" height="3" fill="#a02020" />
          <rect x="17" y="25" width="6" height="0.4" fill="#d0d0d0" />
          <rect x="17" y="27.6" width="6" height="0.4" fill="#601010" />
          <rect x="13" y="29" width="14" height="6" fill="#902020" />
          <rect x="13" y="29" width="14" height="0.5" fill="#c03030" />
          <ellipse cx="20" cy="32" rx="3.5" ry="2" fill="#1a0a0a" />
          <ellipse cx="20" cy="32" rx="3.5" ry="2" fill="none" stroke="#3a1010" strokeWidth="0.3" />
          <rect x="13" y="29" width="14" height="0.4" fill="#e8c547" opacity="0.5" />
          <rect x="26" y="25" width="5" height="3" fill="#a02020" />
          <rect x="26" y="25" width="5" height="0.4" fill="#d0d0d0" />
          <rect x="26" y="27.6" width="5" height="0.4" fill="#601010" />
        </g>
      );
    case "keys":
      return (
        <g className="lpc-instrument lpc-keys">
          <rect x="8" y="29" width="0.6" height="6" fill="#202020" />
          <rect x="31" y="29" width="0.6" height="6" fill="#202020" />
          <rect x="4" y="23" width="32" height="2" fill="#1a1a1a" />
          <rect x="4" y="23" width="32" height="0.5" fill="#3a3a3a" />
          <rect x="6" y="23.7" width="5" height="0.5" fill="#e8c547" />
          {[14, 17, 20, 23].map((x) => (
            <circle key={x} cx={x} cy="24" r="0.5" fill="#606060" />
          ))}
          <rect x="4" y="25" width="32" height="3.5" fill="#f4ebd9" />
          <rect x="4" y="25" width="32" height="0.4" fill="#c0b8a0" />
          <rect x="4" y="28.1" width="32" height="0.4" fill="#a0907a" />
          {[8, 12, 16, 20, 24, 28, 32].map((x) => (
            <rect key={x} x={x} y="25.4" width="0.25" height="3.1" fill="#a09078" />
          ))}
          {[6.5, 10.5, 14.5, 18.5, 22.5, 26.5, 30.5, 34.5].map((x) => (
            <rect key={x} x={x} y="25" width="1.4" height="2" fill="#1a1410" />
          ))}
          {[6.5, 10.5, 14.5, 18.5, 22.5, 26.5, 30.5, 34.5].map((x) => (
            <rect key={`hi-${x}`} x={x} y="25" width="1.4" height="0.3" fill="#3a3034" />
          ))}
        </g>
      );
  }
}
